import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import apiClient from '../api/apiClient';
import useAuth from '../hooks/useAuth';

const SubscriptionContext = createContext(null);

const EMPTY = {
  plan: null,
  planName: null,
  status: null,
  featureKeys: [],
  limits: {},
  agentTier: 'standard',
  isTrial: false,
  daysLeft: null,
  endDate: null,
  planColor: '#1976d2',
  maxUsers: 10,
};

export function SubscriptionProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  const [subscription, setSubscription] = useState(EMPTY);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);

  const isSuperAdmin = user?.isSuperAdmin || user?.role === 'SuperAdmin';
  const companyId    = user?.activeCompanyId || user?.companyId;

  const refresh = useCallback(async () => {
    if (!isAuthenticated || !companyId || isSuperAdmin) {
      setSubscription(EMPTY);
      setLoading(false);
      return;
    }
    try {
      const r = await apiClient.get('/api/billing/my-subscription');
      setSubscription(r.data.subscription || EMPTY);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load subscription');
      setSubscription(EMPTY);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, companyId, isSuperAdmin]);

  useEffect(() => { refresh(); }, [refresh]);

  /** Returns true if the company's plan includes this feature key. SuperAdmin always returns true. */
  const hasFeature = useCallback((key) => {
    if (isSuperAdmin) return true;
    if (!subscription.featureKeys?.length) return false;
    return subscription.featureKeys.includes(key);
  }, [subscription.featureKeys, isSuperAdmin]);

  /** Returns true if subscription is active (not expired/cancelled). */
  const isActive = subscription.status === 'active' || subscription.status === 'trial' || isSuperAdmin;

  /** Days left in trial (null if not on trial or no end date). */
  const trialDaysLeft = subscription.isTrial && subscription.daysLeft != null
    ? subscription.daysLeft
    : null;

  /** Show a warning banner: trial expiry in < 7 days, or subscription expiry in < 30 days. */
  const warningBanner = (() => {
    if (isSuperAdmin) return null;
    if (subscription.isTrial && trialDaysLeft !== null) {
      if (trialDaysLeft <= 0) return { type: 'trial_expired', message: 'Your trial has ended. Please upgrade to continue.' };
      if (trialDaysLeft <= 7) return { type: 'trial_expiring', message: `Your free trial expires in ${trialDaysLeft} day${trialDaysLeft !== 1 ? 's' : ''}. Upgrade now to avoid losing access.` };
    }
    if (!subscription.isTrial && subscription.daysLeft !== null) {
      if (subscription.daysLeft <= 0) return { type: 'expired', message: 'Your subscription has expired. Please renew to restore access.' };
      if (subscription.daysLeft <= 30) return { type: 'expiring', message: `Subscription renews in ${subscription.daysLeft} days. Contact your admin to ensure renewal.` };
    }
    return null;
  })();

  return (
    <SubscriptionContext.Provider value={{
      subscription, loading, error, isActive, hasFeature,
      trialDaysLeft, warningBanner, refresh, isSuperAdmin,
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) throw new Error('useSubscription must be used within SubscriptionProvider');
  return ctx;
}

export default SubscriptionContext;
